import { useId } from "react";
import type { Flow, FlowModel } from "../../lib/flow.js";
import { formatUnit } from "../../lib/format.js";

/**
 * The design's animated flow stage, on this installation's topology.
 *
 * Geometry is the design's, unchanged: a 540 by 180 stage, the left node at
 * x=115, the inverter as a 44-unit rounded square at the centre, the battery
 * as a circle at x=460, the home hanging below the inverter, and the same two
 * conduit paths. The 3:1 stage is also what stops this ballooning -- the
 * previous diagram was 320 by 200 with no maximum width, so across a
 * two-column cell it scaled to roughly 1360 by 850 and rendered its labels at
 * four times their intended size.
 *
 * **Solar is the left node, where the design puts it.** Until slice 5c the
 * grid stood there, because no array existed; since `DECISIONS/0028` the panels
 * are verified and take their place. The grid moves to the bottom left,
 * mirroring the home, with its own conduit into the inverter. The single
 * source-to-battery conduit is split at the inverter, so the battery's beam can
 * run the way the power does: in while charging, out while discharging.
 *
 * Every flow is also written out underneath in words. The animation is not the
 * only carrier of direction or magnitude, which section 4 requires, and the
 * `<desc>` gives the whole picture as one sentence.
 */
export function FlowStage({ model, stale }: { model: FlowModel; stale: boolean }) {
  const base = useId();
  const titleId = `${base}-title`;
  const descId = `${base}-desc`;
  const beam = `${base}-beam`;
  const branch = `${base}-branch`;

  const load = model.flows.find((flow) => flow.id === "load");
  const grid = model.flows.find((flow) => flow.id === "grid");
  const solar = model.flows.find((flow) => flow.id === "solar");
  const battery = model.flows.find((flow) => flow.id === "battery");

  return (
    <svg
      viewBox="0 0 540 180"
      role="img"
      aria-labelledby={titleId}
      aria-describedby={descId}
      focusable="false"
      // Capped, not merely full width. Without a ceiling the stage grows with
      // its column and takes the type with it.
      className="mx-auto w-full max-w-[560px]"
    >
      <title id={titleId}>Power flow</title>
      <desc id={descId}>{model.flows.map((flow) => flow.description).join(". ")}.</desc>

      <defs>
        <linearGradient id={beam} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" className="[stop-color:var(--accent)]" />
          <stop offset="100%" className="[stop-color:var(--accent-2)]" />
        </linearGradient>
        <linearGradient id={branch} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" className="[stop-color:var(--accent-2)]" />
          <stop offset="100%" className="[stop-color:var(--accent)]" />
        </linearGradient>
      </defs>

      {/* Conduits: the route exists whether or not anything is travelling it. */}
      {[SOLAR_TO_INVERTER, INVERTER_TO_BATTERY, GRID_TO_INVERTER].map((d) => (
        <g key={d}>
          <Conduit d={d} width={7} className="stroke-ink/[0.07]" />
          <Conduit d={d} width={2.5} className="stroke-ink/[0.14]" />
        </g>
      ))}
      <Conduit d={INVERTER_TO_HOME} width={6} className="stroke-ink/[0.07]" />
      <Conduit d={INVERTER_TO_HOME} width={2} className="stroke-ink/[0.12]" />

      {/* The live beams. Drawn only where something is actually moving, so a
          still diagram means a still system rather than a broken one. */}
      {isMoving(solar) && <Beam d={SOLAR_TO_INVERTER} stroke={beam} stale={stale} />}
      {isMoving(grid) && <Beam d={GRID_TO_INVERTER} stroke={beam} stale={stale} />}
      {isMoving(battery) && (
        <Beam
          d={battery?.to === "battery" ? INVERTER_TO_BATTERY : BATTERY_TO_INVERTER}
          stroke={beam}
          stale={stale}
        />
      )}
      {isMoving(load) && <Beam d={INVERTER_TO_HOME} stroke={branch} stale={stale} thin />}

      <Node
        x={80}
        label="Solar"
        value={solar === undefined ? "" : formatUnit(solar.watts, "W", true)}
        shape="square"
      />
      <Node x={270} label="Inverter" value={formatUnit(model.inverterTempC, "°C")} shape="square" />
      {/* No caption under the battery. It used to repeat the charge state --
          "Battery charging" -- which the legend below already states in full,
          so the same sentence appeared twice within an inch of itself and any
          assertion for it matched two elements. The legend is the text
          equivalent; the stage is the picture. */}
      <Node x={460} label="Battery" value={`${String(model.socPct)}%`} shape="circle" />

      {/* Home, below the inverter, as the design places it; the grid mirrors
          it at the bottom left. */}
      <LowNode
        x={270}
        label="Home"
        value={load === undefined ? "" : formatUnit(load.watts, "W", true)}
      />
      <LowNode
        x={80}
        label="Grid"
        value={grid === undefined ? "" : formatUnit(grid.watts, "W", true)}
      />
    </svg>
  );
}

const SOLAR_TO_INVERTER = "M 115,70 C 175,70 215,70 250,70";
const INVERTER_TO_BATTERY = "M 290,70 C 325,70 365,70 425,70";
const BATTERY_TO_INVERTER = "M 425,70 C 365,70 325,70 290,70";
const GRID_TO_INVERTER = "M 102,150 C 170,150 228,128 252,94";
const INVERTER_TO_HOME = "M 270,95 L 270,135";

/** A live beam along a conduit, dashed and moving the way the power does. */
function Beam({
  d,
  stroke,
  stale,
  thin = false,
}: {
  d: string;
  stroke: string;
  stale: boolean;
  thin?: boolean;
}) {
  return (
    <path
      d={d}
      stroke={`url(#${stroke})`}
      strokeWidth={thin ? 2 : 2.5}
      strokeLinecap="round"
      fill="none"
      className={stale ? "opacity-30" : "animate-flow-dash"}
      strokeDasharray={thin ? "5 6" : "6 8"}
    />
  );
}

/** A node on the lower row: the design's home tile, a 44 by 34 rounded box. */
function LowNode({ x, label, value }: { x: number; label: string; value: string }) {
  return (
    <g>
      <rect
        x={x - 22}
        y="133"
        width="44"
        height="34"
        rx="12"
        className="fill-card stroke-line"
        strokeWidth="1.5"
      />
      <text x={x} y="128" textAnchor="middle" className="fill-ink-muted text-[11px] font-medium">
        {label}
      </text>
      <text
        x={x}
        y="155"
        textAnchor="middle"
        className="tabular fill-ink text-[11px] font-semibold"
      >
        {value}
      </text>
    </g>
  );
}

function isMoving(flow: Flow | undefined): boolean {
  return flow !== undefined && flow.from !== null && flow.watts > 0;
}

function Conduit({ d, width, className }: { d: string; width: number; className: string }) {
  return <path d={d} strokeWidth={width} strokeLinecap="round" fill="none" className={className} />;
}

function Node({
  x,
  label,
  value,
  shape,
}: {
  x: number;
  label: string;
  value: string;
  shape: "square" | "circle";
}) {
  return (
    <g>
      {shape === "circle" ? (
        <circle cx={x} cy="70" r="24" className="fill-card stroke-line" strokeWidth="1.5" />
      ) : (
        <rect
          x={x - 22}
          y="48"
          width="44"
          height="44"
          rx="12"
          className="fill-card stroke-line"
          strokeWidth="1.5"
        />
      )}
      <text x={x} y="28" textAnchor="middle" className="fill-ink-muted text-[11px] font-medium">
        {label}
      </text>
      <text x={x} y="74" textAnchor="middle" className="tabular fill-ink text-[11px] font-semibold">
        {value}
      </text>
    </g>
  );
}
